/**
 * State Property Manager Component
 * 
 * Manages state properties with smart linking to material artifacts and transitions.
 * Features:
 * - List all properties for a state (base + inherited + custom)
 * - Add new property with type selection
 * - For material properties: select/create material artifact, define condition logic
 * - Visual indicators for property completeness
 * - Dependency tree view showing material artifact connections
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  Table,
  Button,
  Tag,
  Space,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  Tooltip,
  Alert,
  Badge,
  Typography,
  Divider,
  Collapse,
  Tree,
  message
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  LinkOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  EyeOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { apiClient } from '../../services/apiClient';
import styles from './StatePropertyManager.module.css';

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;
const { Panel } = Collapse;

interface PropertyDefinition {
  name: string;
  type: string;
  source: string;
  description: string;
  required: boolean;
  default_value: any;
  validation: Record<string, any>;
  material_artifact_ref?: string;
  material_artifact_name?: string;
  condition: Record<string, any>;
  linked_transition_ref?: string;
  template_default_value?: any;
  editable: boolean;
}

interface StatePropertiesResponse {
  state_id: string;
  base_properties: PropertyDefinition[];
  type_properties: PropertyDefinition[];
  state_properties: PropertyDefinition[];
  inherited_properties: PropertyDefinition[];
  total_count: number;
}

interface DependenciesResponse {
  state_id: string;
  total_properties: number;
  material_dependencies: Array<{
    property_name: string;
    material_artifact_name: string;
    material_artifact_id: string;
    condition: Record<string, any>;
  }>;
  inherited_properties: string[];
  user_input_properties: string[];
  base_properties: string[];
  type_properties: string[];
  state_properties: string[];
}

interface StatePropertyManagerProps {
  stateId: string;
  stateName: string;
  onPropertyUpdated?: () => void;
}

interface ArtifactTypeTemplate {
  type_id: string;
  type_name: string;
  properties: PropertyDefinition[];
  template_states: any[];
  template_transitions: any[];
}

export const StatePropertyManager: React.FC<StatePropertyManagerProps> = ({
  stateId,
  stateName,
  onPropertyUpdated
}) => {
  const [loading, setLoading] = useState(false);
  const [baseProperties, setBaseProperties] = useState<PropertyDefinition[]>([]);
  const [typeProperties, setTypeProperties] = useState<PropertyDefinition[]>([]);
  const [stateProperties, setStateProperties] = useState<PropertyDefinition[]>([]);
  const [inheritedProperties, setInheritedProperties] = useState<PropertyDefinition[]>([]);
  const [dependencies, setDependencies] = useState<DependenciesResponse | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDependenciesModal, setShowDependenciesModal] = useState(false);
  const [editingProperty, setEditingProperty] = useState<PropertyDefinition | null>(null);
  const [form] = Form.useForm();

  const fetchProperties = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/prime-artifact-states/${stateId}/properties`);
      const data: StatePropertiesResponse = response.data;
      setBaseProperties(data.base_properties);
      setTypeProperties(data.type_properties);
      setStateProperties(data.state_properties);
      setInheritedProperties(data.inherited_properties);
    } catch (error) {
      console.error('Error fetching properties:', error);
      message.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  }, [stateId]);

  const fetchDependencies = useCallback(async () => {
    try {
      const response = await apiClient.get(`/prime-artifact-states/${stateId}/dependencies`);
      setDependencies(response.data);
    } catch (error) {
      console.error('Error fetching dependencies:', error);
    }
  }, [stateId]);

  useEffect(() => {
    fetchProperties();
    fetchDependencies();
  }, [fetchProperties, fetchDependencies]);

  const handleAddProperty = async (values: any) => {
    try {
      await apiClient.post(`/prime-artifact-states/${stateId}/properties`, values);
      message.success('Property added successfully');
      setShowAddModal(false);
      form.resetFields();
      fetchProperties();
      onPropertyUpdated?.();
    } catch (error) {
      console.error('Error adding property:', error);
      message.error('Failed to add property');
    }
  };

  const handleEditProperty = async (propertyName: string, values: any) => {
    try {
      await apiClient.put(`/prime-artifact-states/${stateId}/properties/${propertyName}`, values);
      message.success('Property updated successfully');
      setEditingProperty(null);
      form.resetFields();
      fetchProperties();
      onPropertyUpdated?.();
    } catch (error) {
      console.error('Error updating property:', error);
      message.error('Failed to update property');
    }
  };

  const handleDeleteProperty = async (propertyName: string) => {
    try {
      await apiClient.delete(`/prime-artifact-states/${stateId}/properties/${propertyName}`);
      message.success('Property deleted successfully');
      fetchProperties();
      onPropertyUpdated?.();
    } catch (error) {
      console.error('Error deleting property:', error);
      message.error('Failed to delete property');
    }
  };

  const handleLinkTransition = async (propertyName: string) => {
    try {
      const response = await apiClient.post(`/prime-artifact-states/${stateId}/properties/${propertyName}/suggest-transition`);
      const suggestions = response.data;
      
      if (suggestions.suggestions.length > 0 || suggestions.existing_transitions.length > 0) {
        // Show transition linking modal
        message.info('Transition suggestions available - implement linking UI');
      } else {
        message.info('No transition suggestions available');
      }
    } catch (error) {
      console.error('Error getting transition suggestions:', error);
      message.error('Failed to get transition suggestions');
    }
  };

  const getPropertyStatusIcon = (property: PropertyDefinition) => {
    if (property.source === 'inherited') {
      return <InfoCircleOutlined style={{ color: '#1890ff' }} />;
    }
    
    if (property.source === 'material' && property.linked_transition_ref) {
      return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    }
    
    if (property.source === 'material' && !property.linked_transition_ref) {
      return <ExclamationCircleOutlined style={{ color: '#faad14' }} />;
    }
    
    return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
  };

  const getPropertyStatusText = (property: PropertyDefinition) => {
    if (property.source === 'inherited') {
      return 'Inherited';
    }
    
    if (property.source === 'material' && property.linked_transition_ref) {
      return 'Linked';
    }
    
    if (property.source === 'material' && !property.linked_transition_ref) {
      return 'Needs Link';
    }
    
    return 'Complete';
  };

  const getPropertyStatusColor = (property: PropertyDefinition) => {
    if (property.source === 'inherited') {
      return 'blue';
    }
    
    if (property.source === 'material' && property.linked_transition_ref) {
      return 'green';
    }
    
    if (property.source === 'material' && !property.linked_transition_ref) {
      return 'orange';
    }
    
    return 'green';
  };

  // Create combined properties list showing all properties organized by source
  const allProperties = useMemo(() => {
    return [
      ...baseProperties.map(p => ({ ...p, source: 'base' })),
      ...typeProperties.map(p => ({ ...p, source: 'type' })),
      ...stateProperties.map(p => ({ ...p, source: 'state' })),
      ...inheritedProperties.map(p => ({ ...p, source: 'inherited' }))
    ];
  }, [baseProperties, typeProperties, stateProperties, inheritedProperties]);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: PropertyDefinition) => (
        <Space>
          {getPropertyStatusIcon(record)}
          <Text strong={record.source === 'inherited'}>{name}</Text>
        </Space>
      )
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => <Tag color="blue">{type}</Tag>
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      render: (source: string) => {
        const colors = {
          base: 'blue',
          type: 'green',
          state: 'orange',
          inherited: 'purple',
          material: 'cyan',
          user_input: 'magenta'
        };
        return <Tag color={colors[source as keyof typeof colors] || 'default'}>{source}</Tag>;
      }
    },
    {
      title: 'Status',
      key: 'status',
      render: (record: PropertyDefinition) => (
        <Tag color={getPropertyStatusColor(record)}>
          {getPropertyStatusText(record)}
        </Tag>
      )
    },
    {
      title: 'Default Value',
      dataIndex: 'default_value',
      key: 'default_value',
      render: (value: any, record: PropertyDefinition) => {
        if (record.source === 'state_override' && record.template_default_value !== undefined) {
          return (
            <Space direction="vertical" size="small">
              <div>
                <Text strong>Current: </Text>
                <Text code>{String(value || 'null')}</Text>
              </div>
              <div>
                <Text type="secondary">Template: </Text>
                <Text code type="secondary">{String(record.template_default_value || 'null')}</Text>
              </div>
            </Space>
          );
        }
        return <Text code>{String(value || 'null')}</Text>;
      }
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: PropertyDefinition) => (
        <Space>
          {record.source !== 'inherited' && record.source !== 'base' && record.source !== 'type' && (
            <>
              <Tooltip title="Edit">
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => {
                    setEditingProperty(record);
                    form.setFieldsValue(record);
                  }}
                />
              </Tooltip>
              {record.source === 'material' && !record.linked_transition_ref && (
                <Tooltip title="Link Transition">
                  <Button
                    type="text"
                    icon={<LinkOutlined />}
                    onClick={() => handleLinkTransition(record.name)}
                  />
                </Tooltip>
              )}
              <Tooltip title="Delete">
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    Modal.confirm({
                      title: 'Delete Property',
                      content: `Are you sure you want to delete property "${record.name}"?`,
                      onOk: () => handleDeleteProperty(record.name)
                    });
                  }}
                />
              </Tooltip>
            </>
          )}
          {(record.source === 'base' || record.source === 'type') && (
            <Tooltip title="Read-only property">
              <Button type="text" disabled icon={<InfoCircleOutlined />} />
            </Tooltip>
          )}
        </Space>
      )
    }
  ];

  const renderDependencyTree = () => {
    if (!dependencies) return null;

    const treeData = [
      {
        title: 'Base Properties',
        key: 'base',
        children: (dependencies.base_properties || []).map(prop => ({
          title: prop,
          key: `base-${prop}`,
          icon: <Tag color="blue">base</Tag>
        }))
      },
      {
        title: 'Type Properties',
        key: 'type',
        children: (dependencies.type_properties || []).map(prop => ({
          title: prop,
          key: `type-${prop}`,
          icon: <Tag color="green">type</Tag>
        }))
      },
      {
        title: 'State Properties',
        key: 'state',
        children: (dependencies.state_properties || []).map(prop => ({
          title: prop,
          key: `state-${prop}`,
          icon: <Tag color="orange">state</Tag>
        }))
      },
      {
        title: 'Inherited Properties',
        key: 'inherited',
        children: (dependencies.inherited_properties || []).map(prop => ({
          title: prop,
          key: `inherited-${prop}`,
          icon: <Tag color="purple">inherited</Tag>
        }))
      },
      {
        title: 'User Input Properties',
        key: 'user_input',
        children: (dependencies.user_input_properties || []).map(prop => ({
          title: prop,
          key: `user-${prop}`,
          icon: <Tag color="magenta">user_input</Tag>
        }))
      },
      {
        title: 'Material Dependencies',
        key: 'material',
        children: (dependencies.material_dependencies || []).map(dep => ({
          title: `${dep.property_name} → ${dep.material_artifact_name}`,
          key: `material-${dep.property_name}`,
          icon: <Tag color="cyan">material</Tag>
        }))
      }
    ];

    return <Tree treeData={treeData} showIcon />;
  };

  return (
    <div className={styles.statePropertyManager}>
      <Card
        title={
          <Space>
            <SettingOutlined />
            <span>Properties: {stateName}</span>
            <Badge count={allProperties.length} />
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<EyeOutlined />}
              onClick={() => setShowDependenciesModal(true)}
            >
              View Dependencies
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setShowAddModal(true)}
            >
              Add Property
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={allProperties}
          loading={loading}
          rowKey={(record) => `${record.source}-${record.name}`}
          pagination={false}
          size="small"
        />

        {inheritedProperties.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <Title level={5}>Inherited Properties</Title>
            <Space wrap>
              {inheritedProperties.map(prop => (
                <Tag key={prop.name} color="blue" icon={<InfoCircleOutlined />}>
                  {prop.name}
                </Tag>
              ))}
            </Space>
          </div>
        )}

        {/* Add Property Modal */}
        <Modal
          title="Add Property"
          open={showAddModal}
          onCancel={() => {
            setShowAddModal(false);
            form.resetFields();
          }}
          onOk={() => form.submit()}
          width={600}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleAddProperty}
          >
            <Form.Item
              name="name"
              label="Property Name"
              rules={[{ required: true, message: 'Please enter property name' }]}
            >
              <Input placeholder="e.g., has_business_idea" />
            </Form.Item>

            <Form.Item
              name="type"
              label="Property Type"
              rules={[{ required: true, message: 'Please select property type' }]}
            >
              <Select placeholder="Select type">
                <Option value="boolean">Boolean</Option>
                <Option value="number">Number</Option>
                <Option value="string">String</Option>
                <Option value="date">Date</Option>
                <Option value="reference">Reference</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="source"
              label="Property Source"
              rules={[{ required: true, message: 'Please select property source' }]}
            >
              <Select placeholder="Select source">
                <Option value="base">Base Property</Option>
                <Option value="user_input">User Input</Option>
                <Option value="material">Material Artifact</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
            >
              <TextArea rows={2} placeholder="Describe what this property represents" />
            </Form.Item>

            <Form.Item
              name="required"
              label="Required"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>

            <Form.Item
              name="default_value"
              label="Default Value"
            >
              <Input placeholder="Default value (optional)" />
            </Form.Item>
          </Form>
        </Modal>

        {/* Edit Property Modal */}
        <Modal
          title="Edit Property"
          open={!!editingProperty}
          onCancel={() => {
            setEditingProperty(null);
            form.resetFields();
          }}
          onOk={() => form.submit()}
          width={600}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={(values) => editingProperty && handleEditProperty(editingProperty.name, values)}
          >
            <Form.Item
              name="name"
              label="Property Name"
              rules={[{ required: true, message: 'Please enter property name' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="type"
              label="Property Type"
              rules={[{ required: true, message: 'Please select property type' }]}
            >
              <Select>
                <Option value="boolean">Boolean</Option>
                <Option value="number">Number</Option>
                <Option value="string">String</Option>
                <Option value="date">Date</Option>
                <Option value="reference">Reference</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="source"
              label="Property Source"
              rules={[{ required: true, message: 'Please select property source' }]}
            >
              <Select>
                <Option value="base">Base Property</Option>
                <Option value="user_input">User Input</Option>
                <Option value="material">Material Artifact</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
            >
              <TextArea rows={2} />
            </Form.Item>

            <Form.Item
              name="required"
              label="Required"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>

            <Form.Item
              name="default_value"
              label="Default Value"
            >
              <Input />
            </Form.Item>
          </Form>
        </Modal>

        {/* Dependencies Modal */}
        <Modal
          title="State Dependencies"
          open={showDependenciesModal}
          onCancel={() => setShowDependenciesModal(false)}
          footer={null}
          width={800}
        >
          {dependencies && (
            <div>
              <Alert
                message={`Total Properties: ${dependencies.total_properties}`}
                type="info"
                style={{ marginBottom: 16 }}
              />
              
              <Collapse>
                <Panel header="Property Tree" key="tree">
                  {renderDependencyTree()}
                </Panel>
                
                <Panel header="Material Dependencies" key="material">
                  {(dependencies.material_dependencies || []).map((dep, index) => (
                    <Card key={index} size="small" style={{ marginBottom: 8 }}>
                      <Space direction="vertical">
                        <Text strong>{dep.property_name}</Text>
                        <Text type="secondary">→ {dep.material_artifact_name}</Text>
                        <Text code>{JSON.stringify(dep.condition)}</Text>
                      </Space>
                    </Card>
                  ))}
                </Panel>
              </Collapse>
            </div>
          )}
        </Modal>
      </Card>
    </div>
  );
};
